import { EventWithRels, Game } from '@statsbomb/parachute-types';
import { atom } from 'jotai';
import { unwrap } from 'jotai/utils';
import { atomWithSuspenseQuery } from 'jotai-tanstack-query';
import { filtersAtom } from '@/atoms/filters/filter';
import { convertFilterParamsToString, gameEventParams } from '@/utils/api';
import { convertEventDataIntoDescriptions } from '@/utils/atoms/eventData';
import { getIsDateBeforeOrEqualToToday } from '@/utils/date';
import { getFormattedGameName } from '@/utils/games';
import { gameIdAtom } from '../game/game';
import { selectedGameIdForVideosAtom } from '../video';
import { fetchClientAtom } from './client';
import { unwrappedSelectedCycleAtom } from './cycles';

const rawGameGoalEventsAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['gameGoalEvents', get(selectedGameIdForVideosAtom)] as const;

  const queryFn = async ({ queryKey: [, gameId] }: { queryKey: typeof queryKey }) => {
    if (!gameId) return [];

    return (await fetch(
      `/metric/goals_and_own_goals/events${convertFilterParamsToString(gameEventParams(gameId))}`,
    )) as Promise<EventWithRels[]>;
  };

  return { queryKey, queryFn };
});

export const gameGoalEventDescriptionsAtom = atom(async get => {
  const rawGameGoalEvents = await get(rawGameGoalEventsAtom);
  return convertEventDataIntoDescriptions(rawGameGoalEvents.data);
});

const gamesBySelectedCycleAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['gamesData', get(unwrappedSelectedCycleAtom)] as const;

  const queryFn = async ({ queryKey: [_, selectedCycle] }: { queryKey: typeof queryKey }) => {
    if (!selectedCycle) return [];

    return (await fetch(
      `/games${convertFilterParamsToString({
        eq: { cycle_id: selectedCycle.cycle_id },
      })}`,
    )) as Game[];
  };

  return { queryKey, queryFn };
});

const rawSelectedGameAtom = atomWithSuspenseQuery(get => {
  const { fetch } = get(fetchClientAtom);
  const queryKey = ['selectedGameData', get(gameIdAtom)] as const;

  const queryFn = async ({ queryKey: [_, gameId] }: { queryKey: typeof queryKey }) => {
    if (!gameId) return null;

    const games = (await fetch(
      `/games${convertFilterParamsToString({
        limit: 1,
        eq: { game_id: gameId },
      })}`,
    )) as Game[];

    const selectedGame = games[0];
    return selectedGame || null;
  };

  return { queryKey, queryFn };
});

const selectedGameAtom = atom(async get => (await get(rawSelectedGameAtom)).data);

export const unwrappedSelectedGameAtom = unwrap(selectedGameAtom, prev => prev || null);

export const selectedGameNameAtom = atom(async get => {
  const selectedGame = await get(selectedGameAtom);
  return selectedGame?.name || '';
});

export const unwrappedSelectedGameNameAtom = unwrap(selectedGameNameAtom, prev => prev || '');

export const gameTitleAtom = atom(async get => {
  const selectedGame = await get(selectedGameAtom);

  if (selectedGame === null) return '';
  return getFormattedGameName(selectedGame);
});

export const selectedGameIdAtom = atom<number | null>(null);

export const gamesFilteredByTeamsAndDateAtom = atom(async get => {
  const gamesByCycle = (await get(gamesBySelectedCycleAtom)).data;
  const selectedTeamIds = get(filtersAtom).teamIds;

  const gamesFilteredByTeamsAndDate = gamesByCycle.filter(game => {
    const doesGameIncludeSelectedTeams =
      selectedTeamIds.includes(game.home_team_id) || selectedTeamIds.includes(game.away_team_id);

    const isGameDateBeforeOrEqualToToday = getIsDateBeforeOrEqualToToday(game.date);

    return doesGameIncludeSelectedTeams && isGameDateBeforeOrEqualToToday;
  });

  return gamesFilteredByTeamsAndDate;
});
